import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { alpha } from '@mui/material/styles';
import { fetchHotelSuggestions } from './fetchHotelSuggestions';
import countryListJson from '../../data/location-country-list.json';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../../utils/locationSearch';
import { getAuthAutocompleteSlotProps, authOverlayFieldShellSx } from './authLightPanelTheme';
import { LOGIN_ACCENT } from './loginDarkTheme';

const MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;
const ACCENT = LOGIN_ACCENT;

const countryDetailContext = require.context('../../data/location-country-details', false, /\.json$/);
const cachedCountryDetails = new Map();

function normalizeLocationDataset(mod) {
  const payload = mod?.default ?? mod;
  return payload && Array.isArray(payload.countries) ? payload : { countries: [] };
}

function normalizeCountryDetailDataset(mod) {
  const payload = mod?.default ?? mod;
  return {
    code: String(payload?.code || '').trim().toUpperCase(),
    name: String(payload?.name || '').trim(),
    states: Array.isArray(payload?.states) ? payload.states : [],
  };
}

async function loadCountryDetail(countryCode = '') {
  const normalizedCode = String(countryCode || '').trim().toUpperCase();
  if (!normalizedCode) return { code: '', name: '', states: [] };
  if (cachedCountryDetails.has(normalizedCode)) {
    return cachedCountryDetails.get(normalizedCode);
  }
  try {
    const mod = countryDetailContext(`./${normalizedCode}.json`);
    const payload = normalizeCountryDetailDataset(mod);
    cachedCountryDetails.set(normalizedCode, payload);
    return payload;
  } catch {
    return { code: normalizedCode, name: normalizedCode, states: [] };
  }
}

function resolveCountryFromInput(countryLong, locationData) {
  const raw = String(countryLong || '').trim();
  if (!raw) return null;
  const list = locationData?.countries || [];
  return (
    list.find((c) => c.name === raw) ||
    list.find((c) => c.code === raw.toUpperCase()) ||
    list.find((c) => c.name.toLowerCase() === raw.toLowerCase()) ||
    null
  );
}

function inferLocationFromSecondary(secondaryText) {
  const raw = String(secondaryText || '').trim();
  if (!raw) {
    return { city: '', state: '', countryLong: '', address: '', locationFromPlaces: false };
  }
  const parts = raw.split(',').map((x) => x.trim()).filter(Boolean);
  const city = parts[0] || '';
  const countryLong = parts.length > 1 ? parts[parts.length - 1] : '';
  let state = '';
  if (parts.length >= 3) state = parts[parts.length - 2];
  else if (parts.length === 2) state = parts[0];
  return {
    city,
    state,
    countryLong,
    address: raw,
    locationFromPlaces: Boolean(city && state),
  };
}

function getManualDialogStyles(light) {
  const outline = light
    ? {
        border: 'rgba(17,24,39,0.22)',
        borderHover: 'rgba(17,24,39,0.35)',
        borderFocus: alpha(ACCENT, 0.65),
        borderDisabled: 'rgba(17,24,39,0.2)',
        label: '#6b7280',
        labelDisabled: '#9ca3af',
        input: '#111827',
        inputDisabled: '#6b7280',
        helper: '#6b7280',
        bg: '#fff',
        bgDisabled: '#f9fafb',
      }
    : {
        border: 'rgba(255,255,255,0.16)',
        borderHover: 'rgba(255,255,255,0.28)',
        borderFocus: alpha(ACCENT, 0.65),
        borderDisabled: 'rgba(255,255,255,0.14)',
        label: 'rgba(255,255,255,0.58)',
        labelDisabled: 'rgba(255,255,255,0.42)',
        input: '#fff',
        inputDisabled: 'rgba(255,255,255,0.5)',
        helper: 'rgba(255,255,255,0.45)',
        bg: 'rgba(255,255,255,0.03)',
        bgDisabled: 'rgba(255,255,255,0.02)',
      };

  const outlinedInputSx = {
    borderRadius: '12px',
    bgcolor: outline.bg,
    color: outline.input,
    '& fieldset': {
      borderColor: outline.border,
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: outline.borderHover,
    },
    '&.Mui-focused fieldset': {
      borderColor: outline.borderFocus,
      borderWidth: '1px',
    },
    '&.Mui-disabled': {
      bgcolor: outline.bgDisabled,
      '& fieldset': {
        borderColor: `${outline.borderDisabled} !important`,
        borderWidth: '1px !important',
      },
    },
    '&.Mui-disabled input': {
      WebkitTextFillColor: outline.inputDisabled,
      color: outline.inputDisabled,
    },
  };

  const dialogFieldSx = {
    '& .MuiOutlinedInput-root': outlinedInputSx,
    '& .MuiInputLabel-root': { color: outline.label },
    '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
    '& .MuiInputLabel-root.Mui-disabled': { color: outline.labelDisabled },
    '& .MuiFormHelperText-root': { color: outline.helper, mx: 0, mt: 0.5 },
    '& .MuiInputBase-input::placeholder': {
      color: light ? '#9ca3af' : 'rgba(255,255,255,0.45)',
      opacity: 1,
    },
  };

  const locationAutocompletePaperSx = light
    ? {
        bgcolor: '#fff',
        border: '1px solid rgba(17,24,39,0.14)',
        borderRadius: '10px',
        mt: 0.5,
        boxShadow: '0 12px 32px rgba(15,23,42,0.12)',
        '& .MuiAutocomplete-option': { color: '#111827', fontSize: '0.875rem', py: 1 },
        '& .MuiAutocomplete-option[aria-selected="true"]': { bgcolor: alpha(ACCENT, 0.14) },
        '& .MuiAutocomplete-option.Mui-focused': { bgcolor: alpha(ACCENT, 0.08) },
      }
    : {
        bgcolor: '#141414',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '10px',
        mt: 0.5,
        boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
        '& .MuiAutocomplete-option': { color: 'rgba(255,255,255,0.92)', fontSize: '0.875rem', py: 1 },
        '& .MuiAutocomplete-option[aria-selected="true"]': { bgcolor: alpha(ACCENT, 0.14) },
        '& .MuiAutocomplete-option.Mui-focused': { bgcolor: alpha(ACCENT, 0.1) },
      };

  return {
    dialogFieldSx,
    manualLocationAutocompleteSx: {
      ...dialogFieldSx,
      width: '100%',
      '& .MuiAutocomplete-inputRoot': outlinedInputSx,
      '& .MuiAutocomplete-popupIndicator': { color: light ? '#6b7280' : 'rgba(255,255,255,0.55)' },
      '& .MuiAutocomplete-clearIcon': { color: light ? '#9ca3af' : 'rgba(255,255,255,0.45)' },
      '& .MuiAutocomplete-popupIndicator.Mui-disabled': { color: light ? '#d1d5db' : 'rgba(255,255,255,0.28)' },
    },
    locationAutocompletePaperSx,
    dialogPaperSx: light
      ? {
          bgcolor: '#fff',
          border: `1px solid ${alpha(ACCENT, 0.28)}`,
          borderRadius: '16px',
          color: '#111827',
          boxShadow: '0 35px 90px rgba(15,23,42,0.18)',
        }
      : {
          bgcolor: '#101214',
          border: `1px solid ${alpha(ACCENT, 0.28)}`,
          borderRadius: '16px',
          color: '#fff',
          boxShadow: '0 35px 90px rgba(0,0,0,0.68)',
          backgroundImage:
            'radial-gradient(120% 120% at 0% 0%, rgba(76,225,182,0.14) 0%, rgba(16,18,20,0.97) 45%)',
        },
  };
}

export default function PropertyNamePlacesField({
  value,
  onChange,
  onPlaceSelect,
  error = false,
  helperText = '',
  sx,
  light = false,
  compact = false,
  label = 'Property Name',
  allowManualEntry = false,
}) {
  const listId = useId();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const suppressListOnNextFocusRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const [localMessage, setLocalMessage] = useState('');
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualDialogMessage, setManualDialogMessage] = useState('');
  const [manualDialogError, setManualDialogError] = useState('');
  const [selectedCountryDetail, setSelectedCountryDetail] = useState(null);
  const [countryDetailLoading, setCountryDetailLoading] = useState(false);
  const [countryDetailError, setCountryDetailError] = useState('');
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [manualHotelInfo, setManualHotelInfo] = useState({
    propertyName: '',
    address: '',
    city: '',
    state: '',
    countryLong: '',
  });

  const manualQuery = String(value || '').trim();
  const manualEntryAvailable = allowManualEntry && manualQuery.length >= MIN_CHARS;
  const manualStyles = useMemo(() => getManualDialogStyles(light), [light]);
  const locationAutocompleteSlotProps = useMemo(
    () => getAuthAutocompleteSlotProps(light, { paperSx: manualStyles.locationAutocompletePaperSx }),
    [light, manualStyles.locationAutocompletePaperSx]
  );

  const countryOptions = useMemo(
    () => normalizeLocationDataset(countryListJson).countries || [],
    []
  );

  const selectedCountryObj = useMemo(
    () => resolveCountryFromInput(manualHotelInfo.countryLong, { countries: countryOptions }),
    [countryOptions, manualHotelInfo.countryLong]
  );

  const availableStates = useMemo(() => {
    if (!selectedCountryDetail) return [];
    return (selectedCountryDetail.states || []).map((s) => ({ code: s.code, name: s.name }));
  }, [selectedCountryDetail]);

  const availableCities = useMemo(() => {
    if (!selectedCountryDetail || !manualHotelInfo.state) return [];
    const stateObj = (selectedCountryDetail.states || []).find(
      (s) => s.name === manualHotelInfo.state || s.code === manualHotelInfo.state
    );
    return stateObj?.cities || [];
  }, [selectedCountryDetail, manualHotelInfo.state]);

  const cancelScheduledSearch = useCallback(() => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  }, []);

  const openManualInfoDialog = useCallback((hotelName, { secondary = '', message = '' } = {}) => {
    const inferred = inferLocationFromSecondary(secondary);
    setManualHotelInfo({
      propertyName: String(hotelName || '').trim(),
      address: inferred.address || '',
      city: inferred.city || '',
      state: inferred.state || '',
      countryLong: inferred.countryLong || '',
    });
    setManualDialogMessage(
      message || 'Hotel not found in search. Please provide the hotel details manually.'
    );
    setManualDialogError('');
    setCountryDetailError('');
    setManualDialogOpen(true);
    setOpen(false);
    setSuggestions([]);
    setHighlight(-1);
    suppressListOnNextFocusRef.current = true;
    requestAnimationFrame(() => {
      if (inputRef.current && document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedCountryObj?.code) {
      setSelectedCountryDetail(null);
      setCountryDetailLoading(false);
      setCountryDetailError('');
      return undefined;
    }

    let cancelled = false;
    setCountryDetailLoading(true);
    setCountryDetailError('');

    loadCountryDetail(selectedCountryObj.code)
      .then((nextData) => {
        if (cancelled) return;
        setSelectedCountryDetail(nextData);
        if (!nextData?.states?.length) {
          setCountryDetailError('No states found for the selected country.');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSelectedCountryDetail({ code: selectedCountryObj.code, name: selectedCountryObj.name, states: [] });
        setCountryDetailError('Could not load states and cities. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setCountryDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCountryObj]);

  const submitManualHotelInfo = useCallback(() => {
    const propertyName = String(manualHotelInfo.propertyName || '').trim();
    if (!propertyName) {
      setManualDialogError('Hotel name is required.');
      return;
    }

    const address = String(manualHotelInfo.address || '').trim();
    const city = String(manualHotelInfo.city || '').trim();
    const state = String(manualHotelInfo.state || '').trim();
    const countryLong = String(manualHotelInfo.countryLong || '').trim();

    onChange(propertyName);
    setSelectedHotel({
      propertyName,
      address,
      city,
      state,
      countryLong,
      placeId: '',
      source: 'manual',
    });
    setManualDialogOpen(false);
    setManualDialogError('');
    setLocalMessage('Hotel details added manually.');
    setOpen(false);
    setSuggestions([]);
    setHighlight(-1);
    suppressListOnNextFocusRef.current = true;

    onPlaceSelect?.({
      propertyName,
      formattedAddress: address,
      shortFormattedAddress: address,
      placeId: '',
      state,
      city,
      address,
      countryLong,
      locationFromPlaces: false,
    });
  }, [manualHotelInfo, onChange, onPlaceSelect]);

  const runFetch = useCallback(
    (query) => {
      const trimmed = String(query || '').trim();
      if (trimmed.length < MIN_CHARS) {
        setSuggestions([]);
        setOpen(false);
        setLocalMessage('');
        setLoading(false);
        return;
      }

      setLoading(true);
      fetchHotelSuggestions(trimmed, 8)
        .then((results) => {
          setSuggestions(results);
          const canUseManual = allowManualEntry && trimmed.length >= MIN_CHARS;
          setOpen(results.length > 0 || canUseManual);
          if (results.length === 0 && canUseManual) {
            setLocalMessage('Hotel not found in results. Select "Add Hotel" to provide details manually.');
          } else {
            setLocalMessage(results.length === 0 ? 'No properties found. Try another name.' : '');
          }
        })
        .catch(() => {
          setSuggestions([]);
          const canUseManual = allowManualEntry && trimmed.length >= MIN_CHARS;
          setOpen(canUseManual);
          if (canUseManual) {
            setLocalMessage('Could not load hotel search. Select "Add Hotel" to enter details manually.');
          } else {
            setLocalMessage('Could not load property suggestions. Please try again.');
          }
        })
        .finally(() => setLoading(false));
    },
    [allowManualEntry]
  );

  const scheduleSearch = useCallback(
    (query) => {
      cancelScheduledSearch();
      const trimmed = String(query || '').trim();
      if (trimmed.length < MIN_CHARS) {
        setSuggestions([]);
        setOpen(false);
        setLocalMessage('');
        return;
      }
      searchDebounceRef.current = window.setTimeout(() => {
        searchDebounceRef.current = null;
        runFetch(trimmed);
      }, SEARCH_DEBOUNCE_MS);
    },
    [cancelScheduledSearch, runFetch]
  );

  useEffect(
    () => () => cancelScheduledSearch(),
    [cancelScheduledSearch]
  );

  useEffect(() => {
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
        setHighlight(-1);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (item) => {
    cancelScheduledSearch();
    setOpen(false);
    setSuggestions([]);
    setHighlight(-1);
    const inferred = inferLocationFromSecondary(item.secondary);
    const resolvedAddress = String(item.address || inferred.address || '').trim();
    const resolvedCity = String(item.city || inferred.city || '').trim();
    const resolvedState = String(item.state || inferred.state || '').trim();
    const resolvedCountry = String(item.countryLong || inferred.countryLong || '').trim();
    onChange(item.main);
    setSelectedHotel({
      propertyName: item.main,
      address: resolvedAddress,
      city: resolvedCity,
      state: resolvedState,
      countryLong: resolvedCountry,
      placeId: item.placeResource || item.placeId || '',
      source: 'api',
    });
    setLocalMessage('Hotel selected from search results.');
    onPlaceSelect?.({
      propertyName: item.main,
      formattedAddress: resolvedAddress,
      shortFormattedAddress: item.secondary || '',
      placeId: item.placeResource || item.placeId || '',
      state: resolvedState,
      city: resolvedCity,
      address: resolvedAddress,
      countryLong: resolvedCountry,
      locationFromPlaces: Boolean(item.locationFromPlaces ?? inferred.locationFromPlaces),
    });
  };

  const pickManualOption = useCallback(() => {
    const currentQuery = String(value || '').trim();
    if (!currentQuery) return;
    setOpen(false);
    setHighlight(-1);
    openManualInfoDialog(currentQuery, {
      message: 'Hotel not listed? Add hotel details manually to continue.',
    });
  }, [openManualInfoDialog, value]);

  const onInputChange = (event) => {
    const query = event.target.value;
    suppressListOnNextFocusRef.current = false;
    onChange(query);
    if (selectedHotel && query.trim() !== String(selectedHotel.propertyName || '').trim()) {
      setSelectedHotel(null);
    }
    setHighlight(-1);
    setLocalMessage('');
    if (query.trim().length < MIN_CHARS) {
      cancelScheduledSearch();
      setSuggestions([]);
      setOpen(false);
      return;
    }
    scheduleSearch(query);
  };

  const handleSearchClick = () => {
    cancelScheduledSearch();
    suppressListOnNextFocusRef.current = false;
    const query = String(value || '').trim();
    if (query.length < MIN_CHARS) {
      setLocalMessage(`Type at least ${MIN_CHARS} characters to search.`);
      return;
    }
    runFetch(query);
  };

  const onKeyDown = (event) => {
    const totalOptions = suggestions.length + (manualEntryAvailable ? 1 : 0);
    if (event.key === 'Enter') {
      event.preventDefault();
      if (open && totalOptions > 0 && highlight >= 0) {
        if (highlight < suggestions.length) pick(suggestions[highlight]);
        else pickManualOption();
      } else {
        handleSearchClick();
      }
      return;
    }
    if (!open || totalOptions === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((current) => Math.min(current + 1, totalOptions - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  };

  const dropdownBg = light ? '#ffffff' : '#141414';
  const dropdownBorder = light ? 'rgba(17,24,39,0.14)' : 'rgba(255,255,255,0.12)';
  const textPrimary = light ? '#111827' : '#fff';
  const textSecondary = light ? '#6b7280' : 'rgba(255,255,255,0.45)';

  return (
    <Box ref={wrapRef} sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        inputRef={inputRef}
        label={label}
        value={value}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        onFocus={() => {
          if (suppressListOnNextFocusRef.current) return;
          if (suggestions.length > 0 || manualEntryAvailable) setOpen(true);
          else if (String(value || '').trim().length >= MIN_CHARS) scheduleSearch(value);
        }}
        error={error}
        helperText={error ? helperText : selectedHotel ? '' : localMessage || helperText}
        sx={sx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                aria-label={loading ? 'Searching property' : 'Search property'}
                onClick={handleSearchClick}
                edge="end"
                size="small"
                disabled={loading}
                sx={{ color: light ? '#6b7280' : 'rgba(255,255,255,0.55)' }}
              >
                {loading ? <CircularProgress size={18} sx={{ color: ACCENT }} /> : <SearchIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{
          'aria-autocomplete': 'list',
          'aria-controls': open ? listId : undefined,
          'aria-expanded': open,
        }}
      />

      {open && (suggestions.length > 0 || manualEntryAvailable) ? (
        <Paper
          id={listId}
          role="listbox"
          elevation={8}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            zIndex: 1800,
            mt: 0.5,
            maxHeight: compact ? 220 : 260,
            overflow: 'auto',
            bgcolor: dropdownBg,
            border: `1px solid ${dropdownBorder}`,
            borderRadius: 2.5,
            boxShadow: light ? '0 12px 32px rgba(15,23,42,0.12)' : '0 16px 48px rgba(0,0,0,0.55)',
          }}
        >
          <List dense disablePadding>
            {suggestions.map((item, index) => (
              <ListItemButton
                key={item.id}
                role="option"
                selected={highlight === index}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(item)}
                sx={{
                  py: 1.1,
                  px: 1.5,
                  '&.Mui-selected': { bgcolor: alpha(ACCENT, 0.12) },
                  '&:hover': { bgcolor: light ? alpha(ACCENT, 0.08) : 'rgba(255,255,255,0.06)' },
                }}
              >
                <ListItemText
                  primary={item.main}
                  secondary={item.secondary || undefined}
                  primaryTypographyProps={{
                    component: 'span',
                    sx: { display: 'block', color: textPrimary, fontSize: '0.875rem', fontWeight: 600 },
                  }}
                  secondaryTypographyProps={{
                    component: 'span',
                    sx: { display: 'block', color: textSecondary, fontSize: '0.75rem', mt: 0.25 },
                  }}
                />
              </ListItemButton>
            ))}
            {manualEntryAvailable ? (
              <ListItemButton
                role="option"
                selected={highlight === suggestions.length}
                onMouseDown={(event) => event.preventDefault()}
                onClick={pickManualOption}
                sx={{
                  py: 1.1,
                  px: 1.5,
                  borderTop: suggestions.length > 0 ? `1px solid ${dropdownBorder}` : 'none',
                  '&.Mui-selected': { bgcolor: alpha(ACCENT, 0.12) },
                  '&:hover': { bgcolor: light ? alpha(ACCENT, 0.08) : 'rgba(255,255,255,0.06)' },
                }}
              >
                <ListItemText
                  primary="Add Hotel"
                  secondary="Hotel not in list? Add details manually."
                  primaryTypographyProps={{
                    component: 'span',
                    sx: { display: 'block', color: textPrimary, fontSize: '0.875rem', fontWeight: 700 },
                  }}
                  secondaryTypographyProps={{
                    component: 'span',
                    sx: { display: 'block', color: textSecondary, fontSize: '0.75rem', mt: 0.25 },
                  }}
                />
              </ListItemButton>
            ) : null}
          </List>
        </Paper>
      ) : null}

      {selectedHotel ? (
        <Card
          elevation={0}
          sx={{
            mt: 1.5,
            borderRadius: '16px',
            overflow: 'hidden',
            border: light ? '1px solid rgba(0,0,0,0.14)' : `1px solid ${alpha(ACCENT, 0.28)}`,
            bgcolor: light ? '#ffffff' : undefined,
            background: light ? '#ffffff' : `linear-gradient(145deg, ${alpha(ACCENT, 0.14)} 0%, rgba(16,18,20,0.98) 62%)`,
            boxShadow: light
              ? '0 10px 28px rgba(15,23,42,0.1)'
              : '0 14px 36px rgba(0,0,0,0.38)',
            '& .MuiTypography-root': light ? { color: '#000000' } : undefined,
            '& .MuiChip-label': light ? { color: '#000000', fontWeight: 800 } : undefined,
          }}
        >
          <Box
            sx={{
              height: 3,
              background: `linear-gradient(90deg, ${ACCENT} 0%, #2dd4bf 55%, #14b8a6 100%)`,
            }}
          />
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.25}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: light ? 'rgba(0,0,0,0.04)' : alpha(ACCENT, 0.18),
                    border: light ? '1px solid rgba(0,0,0,0.12)' : `1px solid ${alpha(ACCENT, 0.4)}`,
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 23, color: light ? '#000000' : ACCENT }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.55 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 15, color: light ? '#000000' : ACCENT }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: light ? '#000000' : ACCENT,
                      }}
                    >
                      Selected property
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      color: light ? '#000000' : '#ffffff',
                      fontWeight: 800,
                      fontSize: '1.06rem',
                      lineHeight: 1.35,
                      wordBreak: 'break-word',
                    }}
                  >
                    {selectedHotel.propertyName}
                  </Typography>
                  {selectedHotel.address ? (
                    <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 0.85 }}>
                      <LocationOnOutlinedIcon
                        sx={{
                          fontSize: 17,
                          color: light ? '#000000' : 'rgba(255,255,255,0.55)',
                          mt: 0.1,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          color: light ? '#000000' : 'rgba(255,255,255,0.72)',
                          fontSize: '0.86rem',
                          fontWeight: 700,
                          lineHeight: 1.45,
                        }}
                      >
                        {selectedHotel.address}
                      </Typography>
                    </Stack>
                  ) : null}
                  {selectedHotel.city || selectedHotel.state || selectedHotel.countryLong ? (
                    <Typography
                      sx={{
                        mt: selectedHotel.address ? 0.45 : 0.85,
                        ml: selectedHotel.address ? 2.9 : 0,
                        color: light ? '#000000' : 'rgba(255,255,255,0.58)',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        lineHeight: 1.4,
                      }}
                    >
                      {[selectedHotel.city, selectedHotel.state, selectedHotel.countryLong]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
              <Chip
                size="small"
                icon={
                  selectedHotel.source === 'manual' ? (
                    <EditOutlinedIcon sx={{ fontSize: '14px !important', color: light ? '#000000' : undefined }} />
                  ) : (
                    <CheckCircleOutlineIcon sx={{ fontSize: '14px !important', color: light ? '#000000' : undefined }} />
                  )
                }
                label={selectedHotel.source === 'manual' ? 'Manual entry' : 'From search'}
                sx={{
                  height: 26,
                  fontWeight: 800,
                  fontSize: '0.68rem',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  flexShrink: 0,
                  bgcolor: light ? 'rgba(0,0,0,0.05)' : alpha(ACCENT, 0.18),
                  color: light ? '#000000' : ACCENT,
                  border: light ? '1px solid rgba(0,0,0,0.16)' : `1px solid ${alpha(ACCENT, 0.38)}`,
                  '& .MuiChip-icon': { ml: 0.75, color: light ? '#000000' : undefined },
                }}
              />
            </Stack>
            {localMessage && selectedHotel.source === 'manual' ? (
              <>
                <Divider
                  sx={{
                    my: 1.5,
                    borderColor: light ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                  }}
                />
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    px: 1.25,
                    py: 0.9,
                    borderRadius: '10px',
                    bgcolor: light ? 'rgba(0,0,0,0.04)' : alpha(ACCENT, 0.1),
                    border: light ? '1px solid rgba(0,0,0,0.12)' : `1px solid ${alpha(ACCENT, 0.22)}`,
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 17, color: light ? '#000000' : ACCENT }} />
                  <Typography
                    sx={{
                      color: light ? '#000000' : ACCENT,
                      fontSize: '0.84rem',
                      fontWeight: 800,
                      lineHeight: 1.4,
                    }}
                  >
                    {localMessage}
                  </Typography>
                </Stack>
              </>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Dialog
        open={manualDialogOpen}
        onClose={() => {
          setManualDialogOpen(false);
          setManualDialogError('');
        }}
        fullWidth
        maxWidth="sm"
        scroll="body"
        PaperProps={{
          sx: {
            ...manualStyles.dialogPaperSx,
            overflow: 'visible',
            maxHeight: 'calc(100dvh - 32px)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '10px',
                bgcolor: alpha(ACCENT, 0.18),
                border: `1px solid ${alpha(ACCENT, 0.36)}`,
              }}
            >
              <BusinessIcon sx={{ fontSize: 18, color: ACCENT }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.18rem', color: textPrimary }}>
                Hotel Not Found
              </Typography>
              <Typography sx={{ fontSize: '0.76rem', color: textSecondary, lineHeight: 1.45 }}>
                Add hotel details manually to continue
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.5, pt: 1.5, overflow: 'visible', ...authOverlayFieldShellSx }}>
          <Typography
            variant="body2"
            sx={{
              color: light ? '#000000' : 'rgba(255,255,255,0.92)',
              fontWeight: 700,
              lineHeight: 1.5,
            }}
          >
            {manualDialogMessage || 'Please provide hotel details to continue.'}
          </Typography>
          <TextField
            label="Hotel name"
            required
            value={manualHotelInfo.propertyName}
            onChange={(event) => {
              setManualDialogError('');
              setManualHotelInfo((prev) => ({ ...prev, propertyName: event.target.value }));
            }}
            fullWidth
            sx={manualStyles.dialogFieldSx}
          />
          <TextField
            label="Address"
            value={manualHotelInfo.address}
            onChange={(event) => setManualHotelInfo((prev) => ({ ...prev, address: event.target.value }))}
            fullWidth
            multiline
            minRows={2}
            sx={manualStyles.dialogFieldSx}
          />
          <Autocomplete
            options={countryOptions}
            getOptionLabel={(opt) => opt?.name || ''}
            filterOptions={countryFilterOptions}
            value={selectedCountryObj}
            isOptionEqualToValue={(a, b) => a?.code === b?.code}
            onChange={(_event, newVal) => {
              setManualDialogError('');
              setManualHotelInfo((prev) => ({
                ...prev,
                countryLong: newVal ? newVal.name : '',
                state: '',
                city: '',
              }));
            }}
            slotProps={locationAutocompleteSlotProps}
            sx={manualStyles.manualLocationAutocompleteSx}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                placeholder="Search country"
                helperText="Select a country first"
                sx={manualStyles.dialogFieldSx}
              />
            )}
          />
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <Autocomplete
              options={availableStates}
              getOptionLabel={(opt) => opt?.name || ''}
              filterOptions={stateFilterOptions}
              value={
                availableStates.find(
                  (s) => s.name === manualHotelInfo.state || s.code === manualHotelInfo.state
                ) || null
              }
              isOptionEqualToValue={(a, b) => a?.code === b?.code}
              onChange={(_event, newVal) => {
                setManualDialogError('');
                setManualHotelInfo((prev) => ({
                  ...prev,
                  state: newVal ? newVal.name : '',
                  city: '',
                }));
              }}
              loading={countryDetailLoading}
              disabled={!selectedCountryObj || countryDetailLoading}
              slotProps={locationAutocompleteSlotProps}
              sx={manualStyles.manualLocationAutocompleteSx}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State / Province"
                  placeholder="Search state"
                  sx={manualStyles.dialogFieldSx}
                  helperText={
                    !selectedCountryObj
                      ? 'Select country first'
                      : countryDetailLoading
                        ? 'Loading states...'
                        : countryDetailError || 'Then choose state'
                  }
                />
              )}
            />
            <Autocomplete
              freeSolo
              options={availableCities}
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : String(opt || ''))}
              filterOptions={cityFilterOptions}
              value={manualHotelInfo.city || null}
              onChange={(_event, newVal) => {
                setManualDialogError('');
                setManualHotelInfo((prev) => ({
                  ...prev,
                  city: typeof newVal === 'string' ? newVal : newVal || '',
                }));
              }}
              onInputChange={(_event, newInput) => {
                setManualHotelInfo((prev) => ({ ...prev, city: newInput }));
              }}
              loading={countryDetailLoading}
              disabled={!selectedCountryObj || !manualHotelInfo.state || countryDetailLoading}
              slotProps={locationAutocompleteSlotProps}
              sx={manualStyles.manualLocationAutocompleteSx}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  placeholder="Search or type city"
                  sx={manualStyles.dialogFieldSx}
                  helperText={
                    !selectedCountryObj
                      ? 'Select country first'
                      : !manualHotelInfo.state
                        ? 'Select state first'
                        : 'Pick a city or type your own'
                  }
                />
              )}
            />
          </Box>
          {manualDialogError ? (
            <Typography variant="caption" sx={{ color: '#f87171' }}>
              {manualDialogError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.6, pt: 0.5 }}>
          <Button
            onClick={() => {
              setManualDialogOpen(false);
              setManualDialogError('');
            }}
            color="inherit"
            sx={{
              color: light ? '#4b5563' : 'rgba(255,255,255,0.72)',
              border: `1px solid ${light ? 'rgba(17,24,39,0.16)' : 'rgba(255,255,255,0.2)'}`,
              borderRadius: '10px',
              px: 1.8,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={submitManualHotelInfo}
            variant="contained"
            sx={{
              borderRadius: '10px',
              px: 2.3,
              color: '#04110d',
              fontWeight: 700,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#66ffd6' },
            }}
          >
            Use Hotel Details
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
